import { ReactNode } from 'react';

import {
  LayoutCard,
  MetricsBox,
  Skeleton,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

const METRIC_TILE_CORNER_SIZE = 52;

const METRIC_TILE_LAYOUT = {
  root: { sx: { height: '100%' } },
  content: {
    sx: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 128,
      height: '100%',
      // Sparkline chart tooltips render outside the plot area.
      overflow: 'visible',
    },
  },
} as const;

const METRIC_CORNER_SX = {
  position: 'absolute',
  top: 14,
  right: 16,
  width: METRIC_TILE_CORNER_SIZE,
  height: METRIC_TILE_CORNER_SIZE,
  overflow: 'hidden',
  pointerEvents: 'none',
} as const;

const METRIC_CORNER_CLEARANCE = METRIC_TILE_CORNER_SIZE + 20;

const isPlaceholderValue = (value: ReactNode) =>
  value === '—' || value === '-' || value === '' || value == null;

type MetricAmountProps = {
  unit?: ReactNode;
  unitVariant?: 'compact' | 'standard';
  value: ReactNode;
  variant?: 'primary' | 'row';
};

const MetricAmount = ({
  unit,
  unitVariant = 'compact',
  value,
  variant = 'row',
}: MetricAmountProps) => {
  const showUnit = Boolean(unit && !isPlaceholderValue(value));

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={unitVariant === 'compact' ? '2px' : '4px'}
      flexShrink={0}
    >
      <Typography
        variant={variant === 'primary' ? 'h4' : 'A1'}
        color={
          isPlaceholderValue(value)
            ? tokens.theme.fg.structural.secondary
            : tokens.theme.fg.structural.primary
        }
        whiteSpace="nowrap"
      >
        {value}
      </Typography>
      {showUnit ? (
        <Typography
          variant={unitVariant === 'standard' ? 'L12R' : 'L10R'}
          color={tokens.theme.fg.structural.secondary}
          sx={{
            alignSelf: 'flex-end',
            pb: variant === 'primary' ? '2px' : '4px',
            letterSpacing: unitVariant === 'compact' ? '0.05em' : undefined,
          }}
        >
          {unit}
        </Typography>
      ) : null}
    </Stack>
  );
};

export const MetricRowsSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <Stack gap={2}>
    {Array.from({ length: rows }).map((_, index) => (
      <Stack
        key={index}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={12}
        minHeight={17}
      >
        <Skeleton variant="text" width={`${52 + index * 8}%`} height={17} />
        <Skeleton variant="text" width={72} height={17} />
      </Stack>
    ))}
  </Stack>
);

export const ProgressMetricSkeleton = () => (
  <Skeleton
    variant="rounded"
    width="100%"
    height={8}
    sx={{ borderRadius: '4px' }}
  />
);

const FLEET_ROW_MARKER_SIZE = 8;

const FleetRowMarker = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: FLEET_ROW_MARKER_SIZE,
      height: FLEET_ROW_MARKER_SIZE,
      borderRadius: '2px',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

type FleetMetricRowProps = {
  label: ReactNode;
  markerColor?: string;
  unit?: ReactNode;
  unitVariant?: 'compact' | 'standard';
  value: ReactNode;
};

export const FleetMetricRow = ({
  label,
  markerColor,
  unit,
  unitVariant,
  value,
}: FleetMetricRowProps) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    gap={12}
  >
    <Stack direction="row" alignItems="center" gap={4} minWidth={0}>
      {markerColor ? <FleetRowMarker color={markerColor} /> : null}
      <Typography
        component="div"
        variant="L12R"
        color={tokens.theme.fg.structural.secondary}
        textTransform="uppercase"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
    </Stack>
    <MetricAmount
      value={value}
      unit={unit}
      variant="row"
      unitVariant={unitVariant}
    />
  </Stack>
);

type MetricTileCardProps = {
  children: ReactNode;
};

export const MetricTileCard = ({ children }: MetricTileCardProps) => (
  <LayoutCard slotProps={METRIC_TILE_LAYOUT}>{children}</LayoutCard>
);

type FleetMetricTileLoadingState = {
  corner?: ReactNode;
  footer?: ReactNode;
  headerEnd?: ReactNode;
  hideValue?: boolean;
  middle?: ReactNode;
  valueWidth?: number | string;
};

type FleetMetricTileProps = {
  corner?: ReactNode;
  footer?: ReactNode;
  headerEnd?: ReactNode;
  isLoading?: boolean;
  loading?: FleetMetricTileLoadingState;
  middle?: ReactNode;
  pinFooter?: boolean;
  title: ReactNode;
  unit?: ReactNode;
  value?: ReactNode;
};

export const FleetMetricTile = ({
  corner,
  footer,
  headerEnd,
  isLoading = false,
  loading,
  middle,
  pinFooter,
  title,
  unit,
  value,
}: FleetMetricTileProps) => {
  const hasCorner = Boolean(corner || (isLoading && loading?.corner));
  const hasValue = value !== undefined || (isLoading && !loading?.hideValue);
  const hasMiddle = Boolean(middle || (isLoading && loading?.middle));
  const hasFooter = Boolean(footer || (isLoading && loading?.footer));
  const showUnit = Boolean(unit && !isLoading && !isPlaceholderValue(value));
  const shouldPinFooter = pinFooter ?? (hasMiddle || hasFooter);

  return (
    <MetricTileCard>
      {hasCorner ? (
        <Box sx={METRIC_CORNER_SX}>{isLoading ? loading?.corner : corner}</Box>
      ) : null}

      <Stack flexShrink={0} gap="4px" sx={{ position: 'relative' }}>
        {isLoading ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={10}
              minHeight={16}
            >
              <Skeleton variant="text" width="40%" height={16} />
              {loading?.headerEnd}
            </Stack>
            {hasValue ? (
              <Box
                sx={{
                  pr: hasCorner ? `${METRIC_CORNER_CLEARANCE}px` : 0,
                  minHeight: 28,
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={loading?.valueWidth ?? 96}
                  height={28}
                  sx={{ borderRadius: '4px' }}
                />
              </Box>
            ) : null}
          </>
        ) : (
          <>
            {headerEnd ? (
              <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
                {headerEnd}
              </Box>
            ) : null}
            <Box sx={{ pr: hasCorner ? `${METRIC_CORNER_CLEARANCE}px` : 0 }}>
              <MetricsBox
                size="l"
                slots={{
                  label: title,
                  value: hasValue ? value : undefined,
                  unit: showUnit ? unit : undefined,
                }}
                slotProps={{
                  label: {
                    color: tokens.theme.fg.structural.secondary,
                    sx: { textTransform: 'uppercase' },
                  },
                }}
              />
            </Box>
          </>
        )}
      </Stack>

      {hasMiddle ? (
        <Box
          sx={{
            flexShrink: 0,
            mt: '16px',
            maxHeight: 72,
            overflow: 'visible',
          }}
        >
          {isLoading ? loading?.middle : middle}
        </Box>
      ) : null}

      {hasFooter ? (
        <Stack
          sx={{
            flexShrink: 0,
            mt: shouldPinFooter ? 'auto' : hasMiddle || hasValue ? '16px' : 0,
            pt: shouldPinFooter ? (hasMiddle ? '16px' : 0) : 0,
            gap: 2,
            pr: hasCorner ? `${METRIC_CORNER_CLEARANCE}px` : 0,
          }}
        >
          {isLoading ? loading?.footer : footer}
        </Stack>
      ) : null}
    </MetricTileCard>
  );
};
